/**
 * Tool Status Bar Component
 *
 * Displays the status of tools being executed by the VL Chat agent.
 * Shows pills with running/complete status above the assistant message.
 */
import React, { useRef, useEffect } from "react";
import styles from "./ToolStatusBar.module.scss";
import Lottie from "lottie-react";
import loadingDotsAnimation from "../../../assets/animations/LoadingDots.json";
import classNames from "classnames";
import CheckIcon from "../../../assets/icons/CheckIcon";
import ChatTextAnimation from "./ChatTextAnimation";
export interface ToolStatus {
  toolName: string;
  displayName: string;
  status: "running" | "complete";
  message?: string;
}

interface ToolStatusPillProps {
  status: ToolStatus;
}

const ToolStatusPill: React.FC<ToolStatusPillProps> = ({ status }) => {
  const lottieRef = useRef<any>();
  const isRunning = status.status === "running";

  useEffect(() => {
    if (lottieRef.current && isRunning) {
      lottieRef.current.setSpeed(0.7);
    }
  }, [isRunning]);

  return (
    <div
      className={`${styles.pill} ${isRunning ? styles.running : styles.complete}`}
    >
      <span className={styles.indicator}>
        {isRunning ? (
          <Lottie
            lottieRef={lottieRef}
            className={styles.lottie}
            animationData={loadingDotsAnimation}
            loop
            size={50}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
              viewBoxOnly: false,
              // viewBoxSize: "50px",
            }}
          />
        ) : (
          <CheckIcon variant="success" />
        )}
      </span>
      {isRunning ? (
        <ChatTextAnimation
          className={classNames(styles.displayName, styles.displayNameLoading)}
        >
          {status.message || status.displayName}
        </ChatTextAnimation>
      ) : (
        <span className={styles.displayName}>
          {status.message || status.displayName}
        </span>
      )}
    </div>
  );
};

interface ToolStatusBarProps {
  toolStatuses: Map<string, ToolStatus>;
  visible: boolean;
  isThinking?: boolean;
}

export const ToolStatusBar: React.FC<ToolStatusBarProps> = ({
  toolStatuses,
  visible,
  isThinking = false,
}) => {
  // Show if thinking OR if visible with tool statuses
  if (!isThinking && (!visible || toolStatuses.size === 0)) {
    return null;
  }

  return (
    <div className={styles.toolStatusBar}>
      {isThinking && toolStatuses.size === 0 && (
        <ToolStatusPill
          status={{
            toolName: "thinking",
            displayName: "Thinking",
            status: "running",
            message: "Thinking...",
          }}
        />
      )}
      {Array.from(toolStatuses.values()).map((status) => (
        <ToolStatusPill key={status.toolName} status={status} />
      ))}
    </div>
  );
};

export default ToolStatusBar;
